import { useEffect, useState } from "react";
import { api } from "@/lib/api";
import { Button } from "@/components/ui/button";
import { Input } from "@/components/ui/input";
import { Label } from "@/components/ui/label";
import { Select, SelectContent, SelectItem, SelectTrigger, SelectValue } from "@/components/ui/select";
import { Dialog, DialogContent, DialogHeader, DialogTitle, DialogTrigger } from "@/components/ui/dialog";
import { Table, TableBody, TableCell, TableHead, TableHeader, TableRow } from "@/components/ui/table";
import { Badge } from "@/components/ui/badge";
import { toast } from "sonner";
import { Plus, Bell, ReceiptIcon } from "lucide-react";
import CustomerSearchField from "@/components/CustomerSearchField";

function StatusBadge({ s }) {
  const map = { OPEN: "bg-blue-100 text-blue-900", PAID: "bg-emerald-100 text-emerald-900", PARTIALLY_PAID: "bg-amber-100 text-amber-900", OVERDUE: "bg-red-100 text-red-900" };
  return <Badge variant="outline" className={`${map[s] || ""} font-mono text-[10px]`}>{s}</Badge>;
}

export default function Finance({ kind = "RECEIVABLES" }) {
  const isPayables = kind === "PAYABLES";
  const endpoints = isPayables ? {
    invoices: "/procurement/vendor-invoices",
    aging: "/finance/aging?kind=PAYABLES",
    credits: "/finance/debit-notes",
    debits: "/finance/debit-notes",
    payments: "/finance/payments?kind=PAYABLES",
    ledger: "/finance/vendor-ledger",
    postInvoice: "/procurement/vendor-invoices",
    postPayment: "/finance/payments",
    postCreditNote: "/finance/debit-notes",
  } : {
    invoices: "/finance/sales-invoices",
    aging: "/finance/aging",
    credits: "/finance/credit-notes",
    debits: "/finance/debit-notes",
    payments: "/finance/payments",
    ledger: "/finance/customer-ledger",
    postInvoice: "/finance/sales-invoices",
    postPayment: "/finance/payments",
    postCreditNote: "/finance/credit-notes",
  };
  const partyLabel = isPayables ? "Vendor" : "Customer";
  const [tab, setTab] = useState("invoices");
  const [invoices, setInvoices] = useState([]);
  const [aging, setAging] = useState([]);
  const [credits, setCredits] = useState([]);
  const [debits, setDebits] = useState([]);
  const [payments, setPayments] = useState([]);
  const [ledger, setLedger] = useState([]);
  const [ledgerCustomer, setLedgerCustomer] = useState("");
  const [invOpen, setInvOpen] = useState(false);
  const [invForm, setInvForm] = useState({ customer_email: "", customer_name: "", amount: 0, tax_amount: 0, description: "", due_date: "" });
  const [payOpen, setPayOpen] = useState(false);
  const [payForm, setPayForm] = useState({ customer_email: "", customer_name: "", method: "NEFT", amount: 0, reference: "", notes: "" });
  const [cnOpen, setCnOpen] = useState(false);
  const [cnForm, setCnForm] = useState({ customer_email: "", customer_name: "", amount: 0, reason: "", reference_invoice: "" });

  const load = async () => {
    const [i, a, c, d, p] = await Promise.all([
      api.get(endpoints.invoices).catch(() => ({ data: [] })),
      api.get(endpoints.aging).catch(() => ({ data: [] })),
      api.get(endpoints.credits).catch(() => ({ data: [] })),
      api.get(endpoints.debits).catch(() => ({ data: [] })),
      api.get(endpoints.payments).catch(() => ({ data: [] })),
    ]);
    setInvoices(i.data); setAging(a.data); setCredits(c.data); setDebits(d.data); setPayments(p.data);
  };
  useEffect(() => { load(); /* eslint-disable-next-line */ }, [kind]);
  useEffect(() => {
    if (!ledgerCustomer) { setLedger([]); return; }
    // Try the ledger endpoint first; if it comes back empty, fall back to synthesised entries from invoices.
    api.get(endpoints.ledger, { params: { customer_email: ledgerCustomer } })
      .then(({ data }) => {
        if (data && data.length) { setLedger(data); return; }
        // Fallback: derive from invoices
        api.get(endpoints.invoices).then(({ data: invs }) => {
          const filtered = (invs || []).filter((i) => (i.customer_email || i.vendor_email || "").toLowerCase() === ledgerCustomer);
          const synth = filtered.map((i) => ({
            id: i.id,
            date: i.invoice_date || i.date || i.created_at,
            entry_type: isPayables ? "VENDOR_INVOICE" : "INVOICE",
            reference: i.number || i.invoice_number,
            debit: isPayables ? 0 : Number(i.total || i.amount || 0),
            credit: isPayables ? Number(i.total || i.amount || 0) : 0,
            running_balance: null,
            description: i.description || i.notes || "",
          }));
          setLedger(synth);
        }).catch(() => setLedger([]));
      })
      .catch(() => setLedger([]));
  }, [ledgerCustomer, endpoints.ledger, endpoints.invoices, isPayables]);

  const submitInv = async () => {
    try {
      await api.post("/finance/sales-invoices", { ...invForm, amount: Number(invForm.amount), tax_amount: Number(invForm.tax_amount) });
      toast.success("Invoice raised"); setInvOpen(false); load();
    } catch (e) { toast.error(e.response?.data?.detail || "Failed"); }
  };
  const submitPay = async () => {
    try {
      const allocs = invoices.filter((i) => i.customer_email === payForm.customer_email.toLowerCase() && i.outstanding > 0)
        .map((i) => ({ invoice_id: i.id, amount: Math.min(i.outstanding, Number(payForm.amount)) }));
      await api.post("/finance/payments", { ...payForm, amount: Number(payForm.amount), allocations: allocs });
      toast.success("Payment recorded"); setPayOpen(false); load();
    } catch (e) { toast.error(e.response?.data?.detail || "Failed"); }
  };
  const submitCn = async () => {
    try {
      await api.post("/finance/credit-notes", { ...cnForm, amount: Number(cnForm.amount) });
      toast.success("Credit note created"); setCnOpen(false); load();
    } catch (e) { toast.error(e.response?.data?.detail || "Failed"); }
  };
  const remind = async (inv) => {
    try { await api.post("/finance/reminders", { invoice_id: inv.id, level: 1, note: `Outstanding ₹${inv.outstanding.toLocaleString()}` }); toast.success("Reminder sent"); }
    catch (e) { toast.error(e.response?.data?.detail || "Failed"); }
  };
  const cnAction = async (id, action) => { try { await api.post(`/finance/credit-notes/${id}/action`, { action }); toast.success("Done"); load(); } catch (e) { toast.error(e.response?.data?.detail || "Failed"); } };

  const tabs = [
    { k: "invoices", l: "Invoices" }, { k: "aging", l: "Aging" }, { k: "ledger", l: "Customer ledger" },
    { k: "credits", l: "Credit notes" }, { k: "debits", l: "Debit notes" }, { k: "payments", l: "Payments" },
  ];

  return (
    <div className="p-4 sm:p-6 lg:p-8 space-y-4 sm:space-y-6" data-testid="finance-page">
      <div className="flex items-center justify-between flex-wrap gap-3">
        <div>
          <h1 className="font-display text-2xl sm:text-3xl font-black">Finance &amp; accounting</h1>
          <p className="text-muted-foreground">AR, AP, ledgers, aging, credit/debit notes, payments and reminders.</p>
        </div>
        <div className="flex gap-2">
          <Dialog open={invOpen} onOpenChange={setInvOpen}>
            <DialogTrigger asChild><Button data-testid="new-invoice-btn"><Plus className="w-4 h-4 mr-1" /> Invoice</Button></DialogTrigger>
            <DialogContent data-testid="invoice-dialog">
              <DialogHeader><DialogTitle>Raise sales invoice</DialogTitle></DialogHeader>
              <div className="space-y-3">
                <CustomerSearchField
                  testid="inv-customer-search"
                  label="Customer"
                  placeholder="Search by name or email…"
                  value={invForm.customer_email ? { name: invForm.customer_name, email: invForm.customer_email } : null}
                  onChange={(opt) => setInvForm({ ...invForm, customer_email: opt.email || "", customer_name: opt.name || "" })}
                />
                <div className="grid grid-cols-2 gap-3">
                  <div className="space-y-2"><Label>Amount</Label><Input type="number" value={invForm.amount} onChange={(e) => setInvForm({ ...invForm, amount: e.target.value })} data-testid="inv-amt" /></div>
                  <div className="space-y-2"><Label>Tax</Label><Input type="number" value={invForm.tax_amount} onChange={(e) => setInvForm({ ...invForm, tax_amount: e.target.value })} /></div>
                </div>
                <div className="space-y-2"><Label>Due date</Label><Input type="date" value={invForm.due_date} onChange={(e) => setInvForm({ ...invForm, due_date: e.target.value })} /></div>
                <div className="space-y-2"><Label>Description</Label><Input value={invForm.description} onChange={(e) => setInvForm({ ...invForm, description: e.target.value })} /></div>
                <Button onClick={submitInv} data-testid="inv-save">Raise invoice</Button>
              </div>
            </DialogContent>
          </Dialog>
          <Dialog open={payOpen} onOpenChange={setPayOpen}>
            <DialogTrigger asChild><Button variant="outline" data-testid="new-pay-btn">Record payment</Button></DialogTrigger>
            <DialogContent data-testid="pay-dialog">
              <DialogHeader><DialogTitle>Record customer payment</DialogTitle></DialogHeader>
              <div className="space-y-3">
                <CustomerSearchField
                  testid="pay-customer-search"
                  label="Customer"
                  value={payForm.customer_email ? { name: payForm.customer_name, email: payForm.customer_email } : null}
                  onChange={(opt) => setPayForm({ ...payForm, customer_email: opt.email || "", customer_name: opt.name || "" })}
                />
                <div className="grid grid-cols-2 gap-3">
                  <div className="space-y-2"><Label>Amount</Label><Input type="number" value={payForm.amount} onChange={(e) => setPayForm({ ...payForm, amount: e.target.value })} data-testid="pay-amt" /></div>
                  <div className="space-y-2"><Label>Method</Label>
                    <Select value={payForm.method} onValueChange={(v) => setPayForm({ ...payForm, method: v })}>
                      <SelectTrigger data-testid="pay-method"><SelectValue /></SelectTrigger>
                      <SelectContent>{["NEFT", "RTGS", "IMPS", "BANK_TRANSFER", "CHEQUE", "CASH"].map((m) => <SelectItem key={m} value={m}>{m}</SelectItem>)}</SelectContent>
                    </Select>
                  </div>
                </div>
                <div className="space-y-2"><Label>Reference (UTR/cheque)</Label><Input value={payForm.reference} onChange={(e) => setPayForm({ ...payForm, reference: e.target.value })} /></div>
                <p className="text-xs text-muted-foreground">Will auto-allocate against open invoices for this customer (oldest first).</p>
                <Button onClick={submitPay} data-testid="pay-save">Record &amp; allocate</Button>
              </div>
            </DialogContent>
          </Dialog>
          <Dialog open={cnOpen} onOpenChange={setCnOpen}>
            <DialogTrigger asChild><Button variant="outline" data-testid="new-cn-btn">Credit note</Button></DialogTrigger>
            <DialogContent data-testid="cn-dialog">
              <DialogHeader><DialogTitle>Issue credit note</DialogTitle></DialogHeader>
              <div className="space-y-3">
                <CustomerSearchField
                  testid="cn-customer-search"
                  label="Customer"
                  value={cnForm.customer_email ? { name: cnForm.customer_name, email: cnForm.customer_email } : null}
                  onChange={(opt) => setCnForm({ ...cnForm, customer_email: opt.email || "", customer_name: opt.name || "" })}
                />
                <div className="space-y-2"><Label>Reference invoice</Label><Input value={cnForm.reference_invoice} onChange={(e) => setCnForm({ ...cnForm, reference_invoice: e.target.value })} /></div>
                <div className="space-y-2"><Label>Amount</Label><Input type="number" value={cnForm.amount} onChange={(e) => setCnForm({ ...cnForm, amount: e.target.value })} /></div>
                <div className="space-y-2"><Label>Reason</Label><Input value={cnForm.reason} onChange={(e) => setCnForm({ ...cnForm, reason: e.target.value })} /></div>
                <Button onClick={submitCn} data-testid="cn-save">Save credit note (draft)</Button>
              </div>
            </DialogContent>
          </Dialog>
        </div>
      </div>

      <div className="flex gap-2 border-b border-border" data-testid="finance-tabs">
        {tabs.map((t) => (
          <button key={t.k} onClick={() => setTab(t.k)} className={`px-3 py-2 text-sm font-semibold ${tab === t.k ? "border-b-2 border-primary text-foreground" : "text-muted-foreground hover:text-foreground"}`} data-testid={`fin-tab-${t.k}`}>{t.l}</button>
        ))}
      </div>

      {tab === "invoices" && (
        <div className="surface-card p-4 overflow-x-auto">
          <Table>
            <TableHeader><TableRow>
              <TableHead>Number</TableHead>
              <TableHead>{partyLabel}</TableHead>
              <TableHead>{isPayables ? "GRN / PO" : "Dispatch / SO"}</TableHead>
              <TableHead>Date</TableHead>
              <TableHead>Due</TableHead>
              <TableHead>Total</TableHead>
              <TableHead>Outstanding</TableHead>
              <TableHead>Status</TableHead>
              <TableHead></TableHead>
            </TableRow></TableHeader>
            <TableBody>
              {invoices.map((i) => {
                const partyName = isPayables ? (i.vendor_name || i.supplier_name || i.customer_name) : i.customer_name;
                const partySub = isPayables ? (i.vendor_email || i.vendor_gstin || "") : (i.customer_email || "");
                const linkedTop = isPayables ? (i.grn_number || "—") : (i.dispatch_number || "—");
                const linkedSub = isPayables ? (i.po_number || "") : (i.sales_order_number || "");
                return (
                <TableRow key={i.id} data-testid={`inv-row-${i.id}`}>
                  <TableCell className="font-mono text-xs">{i.number}</TableCell>
                  <TableCell><div className="text-sm font-medium">{partyName || "—"}</div><div className="text-xs text-muted-foreground">{partySub}</div></TableCell>
                  <TableCell className="font-mono text-xs">
                    <div>{linkedTop}</div>
                    {linkedSub ? <div className="text-[10px] text-muted-foreground">{linkedSub}</div> : null}
                  </TableCell>
                  <TableCell className="text-xs">{i.invoice_date ? new Date(i.invoice_date).toLocaleDateString() : "—"}</TableCell>
                  <TableCell className="text-xs">{i.due_date ? new Date(i.due_date).toLocaleDateString() : "—"}</TableCell>
                  <TableCell className="font-mono">₹{Number(i.total || 0).toLocaleString()}</TableCell>
                  <TableCell className="font-mono">₹{Number(i.outstanding || 0).toLocaleString()}</TableCell>
                  <TableCell><StatusBadge s={i.status} /></TableCell>
                  <TableCell>{!isPayables && Number(i.outstanding || 0) > 0 && <Button size="sm" variant="outline" onClick={() => remind(i)} data-testid={`remind-${i.id}`}><Bell className="w-3 h-3 mr-1" /> Remind</Button>}</TableCell>
                </TableRow>
              );})}
              {invoices.length === 0 && <TableRow><TableCell colSpan={9} className="text-center text-muted-foreground py-8">No invoices yet.</TableCell></TableRow>}
            </TableBody>
          </Table>
        </div>
      )}

      {tab === "aging" && (
        <div className="surface-card p-4 overflow-x-auto" data-testid="aging-table">
          <Table>
            <TableHeader><TableRow><TableHead>{partyLabel}</TableHead><TableHead>0-30</TableHead><TableHead>31-60</TableHead><TableHead>61-90</TableHead><TableHead>91-180</TableHead><TableHead>180+</TableHead><TableHead>Total</TableHead></TableRow></TableHeader>
            <TableBody>
              {aging.map((a) => {
                const name = isPayables ? (a.vendor_name || a.customer_name) : a.customer_name;
                const email = isPayables ? (a.vendor_email || a.customer_email) : a.customer_email;
                return (
                <TableRow key={email || name}>
                  <TableCell><div className="text-sm font-medium">{name}</div><div className="text-xs text-muted-foreground">{email}</div></TableCell>
                  <TableCell className="font-mono">₹{a.b0_30.toLocaleString()}</TableCell>
                  <TableCell className="font-mono">₹{a.b31_60.toLocaleString()}</TableCell>
                  <TableCell className="font-mono">₹{a.b61_90.toLocaleString()}</TableCell>
                  <TableCell className="font-mono">₹{a.b91_180.toLocaleString()}</TableCell>
                  <TableCell className="font-mono">₹{a.b180_plus.toLocaleString()}</TableCell>
                  <TableCell className="font-mono font-bold">₹{a.total.toLocaleString()}</TableCell>
                </TableRow>
              );})}
              {aging.length === 0 && <TableRow><TableCell colSpan={7} className="text-center text-muted-foreground py-8">No outstanding invoices.</TableCell></TableRow>}
            </TableBody>
          </Table>
        </div>
      )}

      {tab === "ledger" && (
        <div className="space-y-3">
          <div className="max-w-md">
            <CustomerSearchField
              testid="ledger-customer-search"
              label="Customer"
              placeholder="Search by name or email…"
              onChange={(opt) => setLedgerCustomer((opt.email || "").toLowerCase())}
            />
          </div>
          <div className="surface-card p-4 overflow-x-auto">
            <Table>
              <TableHeader><TableRow><TableHead>Date</TableHead><TableHead>Ref</TableHead><TableHead>Type</TableHead><TableHead>Debit</TableHead><TableHead>Credit</TableHead><TableHead>Balance</TableHead></TableRow></TableHeader>
              <TableBody>
                {ledger.map((e) => (
                  <TableRow key={e.id}>
                    <TableCell className="text-xs">{new Date(e.date).toLocaleDateString()}</TableCell>
                    <TableCell className="font-mono text-xs">{e.reference_number}</TableCell>
                    <TableCell><Badge variant="outline" className="font-mono text-[10px]">{e.entry_type}</Badge></TableCell>
                    <TableCell className="font-mono">{e.debit ? `₹${e.debit.toLocaleString()}` : "—"}</TableCell>
                    <TableCell className="font-mono">{e.credit ? `₹${e.credit.toLocaleString()}` : "—"}</TableCell>
                    <TableCell className="font-mono font-bold">₹{e.running_balance.toLocaleString()}</TableCell>
                  </TableRow>
                ))}
                {ledger.length === 0 && <TableRow><TableCell colSpan={6} className="text-center text-muted-foreground py-8">Enter a customer email above.</TableCell></TableRow>}
              </TableBody>
            </Table>
          </div>
        </div>
      )}

      {tab === "credits" && (
        <div className="surface-card p-4 overflow-x-auto">
          <Table>
            <TableHeader><TableRow><TableHead>Number</TableHead><TableHead>{partyLabel}</TableHead><TableHead>Amount</TableHead><TableHead>Reason</TableHead><TableHead>Status</TableHead><TableHead></TableHead></TableRow></TableHeader>
            <TableBody>
              {credits.map((c) => (
                <TableRow key={c.id} data-testid={`cn-row-${c.id}`}>
                  <TableCell className="font-mono text-xs">{c.number}</TableCell>
                  <TableCell className="text-sm">{isPayables ? (c.vendor_name || c.customer_name) : c.customer_name}</TableCell>
                  <TableCell className="font-mono">₹{Number(c.amount).toLocaleString()}</TableCell>
                  <TableCell className="text-sm">{c.reason}</TableCell>
                  <TableCell><Badge variant="outline" className="font-mono text-[10px]">{c.status}</Badge></TableCell>
                  <TableCell className="space-x-1">
                    {c.status === "DRAFT" && <Button size="sm" variant="outline" onClick={() => cnAction(c.id, "SUBMIT")} data-testid={`cn-submit-${c.id}`}>Submit</Button>}
                    {c.status === "SUBMITTED" && <Button size="sm" onClick={() => cnAction(c.id, "ACCEPT")} data-testid={`cn-accept-${c.id}`}>Accept</Button>}
                    {c.status === "SUBMITTED" && <Button size="sm" variant="ghost" onClick={() => cnAction(c.id, "REJECT")}>Reject</Button>}
                  </TableCell>
                </TableRow>
              ))}
              {credits.length === 0 && <TableRow><TableCell colSpan={6} className="text-center text-muted-foreground py-8">No {isPayables ? "debit" : "credit"} notes.</TableCell></TableRow>}
            </TableBody>
          </Table>
        </div>
      )}

      {tab === "debits" && (
        <div className="surface-card p-4 text-sm text-muted-foreground">Debit notes (vendor-side) — POST /api/finance/debit-notes to create.</div>
      )}

      {tab === "payments" && (
        <div className="surface-card p-4 overflow-x-auto">
          <Table>
            <TableHeader><TableRow><TableHead>Number</TableHead><TableHead>{partyLabel}</TableHead><TableHead>Method</TableHead><TableHead>Amount</TableHead><TableHead>Ref</TableHead><TableHead>Date</TableHead></TableRow></TableHeader>
            <TableBody>
              {payments.map((p) => (
                <TableRow key={p.id} data-testid={`pay-row-${p.id}`}>
                  <TableCell className="font-mono text-xs">{p.number}</TableCell>
                  <TableCell className="text-sm">{isPayables ? (p.vendor_name || p.customer_name) : p.customer_name}</TableCell>
                  <TableCell><Badge variant="outline" className="font-mono text-[10px]">{p.method}</Badge></TableCell>
                  <TableCell className="font-mono">₹{Number(p.amount).toLocaleString()}</TableCell>
                  <TableCell className="text-xs font-mono">{p.reference || "—"}</TableCell>
                  <TableCell className="text-xs">{p.date ? new Date(p.date).toLocaleDateString() : "—"}</TableCell>
                </TableRow>
              ))}
              {payments.length === 0 && <TableRow><TableCell colSpan={6} className="text-center text-muted-foreground py-8">No payments yet.</TableCell></TableRow>}
            </TableBody>
          </Table>
        </div>
      )}
    </div>
  );
}
